import type { FieldDefinition } from "@estelle/domain";
import type { ReactNode } from "react";

import { StatusPill } from "@/components/table/StatusPill";
import { humanise } from "@/components/table/tint";
import { Badge } from "@/components/ui/badge";

/**
 * Money is formatted, never computed.
 *
 * The value arrives as the decimal string Postgres `numeric` returns. It is
 * parsed here only to group the digits — no arithmetic happens on the client,
 * which is the whole reason money is a string in the first place.
 */
export function formatMoney(value: string): string {
  const amount = Number(value);
  if (Number.isNaN(amount)) return value;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Dates render in the shop's timezone, not the browser's.
 *
 * Everyone reading this screen is working Indian hours against a business
 * whose day boundaries are IST. A browser in another zone showing an order as
 * having arrived "yesterday" would be describing a different day than the one
 * the warehouse packed to.
 */
export function formatDate(value: string, withTime: boolean): string {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(parsed);
}

/**
 * A projection value is `unknown`, and only scalars have a sensible textual
 * form. Anything else renders as a dash rather than `[object Object]` — a
 * visible gap is a bug someone fixes, where that string is one people learn
 * to ignore.
 */
function asText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  return "";
}

/**
 * Numbers and money are right-aligned; everything else is not.
 *
 * A column of figures that do not share a decimal position cannot be scanned,
 * which is most of what someone does with a stock or totals column.
 */
export function isNumericField(field: FieldDefinition): boolean {
  return field.type === "number" || field.type === "money";
}

/**
 * How a value of each field type is drawn.
 *
 * One place, so every module list renders a date, a total and a status the
 * same way. A module that formats its own is how two screens end up
 * disagreeing about what "08 Sep" means.
 */
export function renderCell(field: FieldDefinition, value: unknown): ReactNode {
  if (value === null || value === undefined || value === "") {
    // An em dash, not an empty cell: a blank reads as a rendering failure,
    // where a dash reads as "there is nothing here", which is the truth.
    return <span className="text-muted-foreground">—</span>;
  }

  switch (field.type) {
    case "money":
      return typeof value === "string" ? formatMoney(value) : asText(value);

    case "date":
    case "datetime":
      return typeof value === "string"
        ? formatDate(value, field.type === "datetime")
        : asText(value);

    case "boolean":
      return value === true ? "Yes" : "No";

    case "enum": {
      const text = asText(value);
      const label =
        field.options?.find((option) => option.value === text)?.label ??
        humanise(text);
      return <StatusPill value={text} label={label} />;
    }

    case "tag": {
      if (!Array.isArray(value)) return asText(value);
      if (value.length === 0) {
        return <span className="text-muted-foreground">—</span>;
      }
      return (
        <span className="flex flex-wrap gap-1">
          {(field.showAllTags ? value : value.slice(0, 3)).map((tag) => (
            <Badge
              key={asText(tag)}
              variant="secondary"
              className="font-normal"
            >
              {asText(tag)}
            </Badge>
          ))}
          {/* Three then a count: a row whose tag list wraps to four lines
              destroys the scannability the whole table is for. */}
          {!field.showAllTags && value.length > 3 ? (
            <span className="text-muted-foreground text-xs">
              +{value.length - 3}
            </span>
          ) : null}
        </span>
      );
    }

    case "number":
      return asText(value);

    default:
      return asText(value);
  }
}
